import { AlertCircle, RefreshCw } from "lucide-react";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { formatApiError } from "@/lib/api-error";
import { cn } from "@/lib/utils";

interface ApiErrorBannerProps {
  error: unknown;
  /** Título override (opcional) — se omitido, usa o derivado pelo status. */
  title?: string;
  /** Handler do botão "Tentar novamente" — se omitido, botão é ocultado. */
  onRetry?: () => void;
  className?: string;
  /** Variante visual: card (default) ou inline (sem moldura grande). */
  variant?: "card" | "inline";
}

/**
 * Banner padrão pra mostrar erros de chamadas à API com a INFORMAÇÃO REAL do
 * servidor (status + `detail` do FastAPI), em vez de mensagens genéricas tipo
 * "HTTP 500 GET …" ou "Nenhum resultado encontrado" encobrindo o erro.
 *
 * Use sempre que um `useQuery`/`useMutation` falhar com erro de
 * `apiFetch` — nunca traduza erro como "lista vazia".
 */
export function ApiErrorBanner({
  error,
  title,
  onRetry,
  className,
  variant = "card",
}: ApiErrorBannerProps) {
  const formatted = formatApiError(error);
  const resolvedTitle = title ?? formatted.title;
  const showStatus = typeof formatted.status === "number";

  return (
    <Alert variant="destructive" className={cn("space-y-2", className)}>
      <AlertCircle className="h-4 w-4" />
      <AlertTitle className="flex items-center gap-2">
        {resolvedTitle}
        {showStatus && (
          <span className="font-mono text-[10px] opacity-70">
            HTTP {formatted.status}
          </span>
        )}
      </AlertTitle>
      <AlertDescription className="space-y-3">
        <p className="text-sm whitespace-pre-wrap wrap-break-word">
          {formatted.detail}
        </p>

        {formatted.rawBody && formatted.rawBody !== formatted.detail && (
          <details className="text-xs">
            <summary className="cursor-pointer opacity-70 hover:opacity-100">
              Ver detalhes técnicos
            </summary>
            <pre className="mt-1 max-h-40 overflow-auto rounded bg-black/5 p-2 font-mono text-[11px]">
              {formatted.rawBody}
            </pre>
          </details>
        )}

        {onRetry && variant === "card" && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={onRetry}
            className="border-current"
          >
            <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
            Tentar novamente
          </Button>
        )}
      </AlertDescription>
    </Alert>
  );
}
